// "Remember for me" (U6): notes, pins and one-press reminders (/api/remember). All local; nothing
// here reaches Gmail, and notes never reach the model.
import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import type { MemoryView, ReminderResult } from "@shared/api";
import { api } from "./api";

const enc = encodeURIComponent;

export const rememberUrls = {
  card: (cardId: string) => `/api/remember/cards/${enc(cardId)}`,
  pin: (cardId: string) => `/api/remember/cards/${enc(cardId)}/pin`,
  deadlineReminder: (cardId: string) => `/api/remember/cards/${enc(cardId)}/deadline-reminder`,
  thread: (threadKey: string) => `/api/remember/threads/${enc(threadKey)}`,
  note: (threadKey: string) => `/api/remember/threads/${enc(threadKey)}/note`,
};

export const rememberApi = {
  card: (cardId: string, signal?: AbortSignal) =>
    api<MemoryView>(rememberUrls.card(cardId), { signal }),
  thread: (threadKey: string, signal?: AbortSignal) =>
    api<MemoryView>(rememberUrls.thread(threadKey), { signal }),
  saveNote: (threadKey: string, text: string) =>
    api<MemoryView>(rememberUrls.note(threadKey), { method: "PUT", body: { text } }),
  pin: (cardId: string, pinned: boolean) =>
    api<MemoryView>(rememberUrls.pin(cardId), { method: "PUT", body: { pinned } }),
  deadlineReminder: (cardId: string) =>
    api<ReminderResult>(rememberUrls.deadlineReminder(cardId), { method: "POST" }),
};

export const rememberKeys = {
  /** Under "remember" so one invalidation refreshes every card and thread view of it. */
  card: (cardId: string) => ["remember", "card", cardId] as const,
  thread: (threadKey: string) => ["remember", "thread", threadKey] as const,
};

export function cardMemoryQuery(cardId: string) {
  return queryOptions({
    queryKey: rememberKeys.card(cardId),
    queryFn: ({ signal }) => rememberApi.card(cardId, signal),
    staleTime: 30_000,
  });
}

export function useCardMemory(cardId: string | null) {
  return useQuery({ ...cardMemoryQuery(cardId ?? ""), enabled: !!cardId });
}

export function useThreadMemory(threadKey: string | null) {
  return useQuery({
    queryKey: rememberKeys.thread(threadKey ?? ""),
    queryFn: ({ signal }) => rememberApi.thread(threadKey!, signal),
    enabled: !!threadKey,
    staleTime: 30_000,
  });
}

export function invalidateMemory(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["remember"] });
}
